import { useCallback, useEffect, useRef, useState } from "react";
import { DateRangePicker, Modal } from "rsuite";
import { FilterPanel, Title, Checkbox, Button, Input } from "../../components";
import { InboxTable } from "./components";
import useTableData from "../../hooks/useTableData";
import { useInboxService, InboxServiceProvider } from "./contexts/InboxServiceContext";
import { EntriesServiceProvider } from "./components/expanded-row/context/entries.context";
import { useAuthenticationService } from "../../services/authentication.service";
import { events, statusCodes, roles } from "../../utils/constants";
import "./index.css";

function InboxPageContent() {
  const inboxService = useInboxService();
  const authService = useAuthenticationService();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");

  function resetAll() {
    setDeleting(true);
    inboxService.resetAll().finally(() => {
      setDeleting(false);
      setDeleteModalOpen(false);
      setResetConfirmText("");
    });
  }

  // Get specific status codes for default filter: 99, 100, 200, and all 500-series error codes
  const getDefaultStatusCodeFilters = useCallback(() => {
    return Object.keys(statusCodes)
      .map((code) => parseInt(code))
      .filter((code) => {
        return code === 99 || code === 100 || code === 200 || (code >= 500 && code < 600);
      });
  }, []);

  // Get matched status code (300)
  const getMatchedStatusCodeFilters = useCallback(() => {
    return [300];
  }, []);

  // Toggle state for current vs past records
  const [showPastRecords, setShowPastRecords] = useState(false);

  const {
    data,
    sortColumn,
    sortType,
    loading,
    activeFilters,
    filtersRef,
    currentPage,
    pageSize,
    totalItems,
    handleSortColumn,
    applyFilters,
    getColumnValues,
    setFilterValues,
    getFilterValues,
    handleDateRangeChange,
    handlePageChange,
    handlePageSizeChange,
    dateRange,
  } = useTableData({
    service: inboxService,
    initialFilters: {
      emailSubject: [],
      dateProcessed: [],
      statusCode: getDefaultStatusCodeFilters(), // Default to 99, 100, 200, and 500-series codes
      statusMessage: [],
      exported: ["false"],
    },
    hasDateRange: true,
    serviceUpdateEvent: events.INBOX_UPDATED,
    filterableColumns: ["emailSubject", "statusCode", "statusMessage", "exported"],
  });

  // Handle toggle between current and past records
  const handleRecordTypeToggle = useCallback(
    (event) => {
      const checked = event?.target?.checked;
      setShowPastRecords(checked);

      // Batch the filter updates to avoid multiple re-renders
      const newFilters = {
        ...filtersRef.current,
        statusCode: checked ? getMatchedStatusCodeFilters() : getDefaultStatusCodeFilters(),
        exported: [checked ? "true" : "false"],
      };

      filtersRef.current = newFilters;
      applyFilters();
    },
    [getDefaultStatusCodeFilters, getMatchedStatusCodeFilters, applyFilters]
  );

  // Auto-sync checkbox state with filter state
  useEffect(() => {
    const currentFilters = filtersRef.current;

    // Check if filters match "past records" criteria
    const isPastRecordsState = currentFilters?.statusCode?.includes(300) && currentFilters.exported.includes(true);

    // Only update if the checkbox state doesn't match the filter state
    setShowPastRecords(isPastRecordsState);
  }, [activeFilters, filtersRef]);

  // Ensure initial filters are applied to URL and activeFilters on mount
  useEffect(() => {
    // Only apply if there are initial filters set and not already in the URL
    const hasInitialFilters = Object.values(filtersRef.current).some((arr) => Array.isArray(arr) && arr.length > 0 && arr.some((v) => v));
    if (hasInitialFilters) {
      applyFilters();
    }
    // eslint-disable-next-line
  }, []);

  // UI state for the filter panel
  const activeFilterColumnKey = useRef(null);
  const [isCheckPickerOpen, setIsCheckPickerOpen] = useState(false);

  const openCheckPickerForColumn = useCallback(
    (columnKey) => {
      activeFilterColumnKey.current = columnKey;
      setFilterValues(filtersRef.current[columnKey] || []);
      setIsCheckPickerOpen(true);
    },
    [setFilterValues]
  );

  const applyCurrentFilters = useCallback(() => {
    const selectedValuesFromPanel = getFilterValues();
    filtersRef.current = {
      ...filtersRef.current,
      [activeFilterColumnKey.current]: selectedValuesFromPanel,
    };
    applyFilters();
    setIsCheckPickerOpen(false);
  }, [getFilterValues, applyFilters]);

  const onFilterChange = useCallback(
    (selectedValues) => {
      setFilterValues(selectedValues);
    },
    [setFilterValues]
  );

  const cancelFiltering = useCallback(() => {
    setIsCheckPickerOpen(false);
  }, []);

  const getValuesForActiveColumn = useCallback(() => {
    if (!activeFilterColumnKey.current) return [];
    return getColumnValues(activeFilterColumnKey.current);
  }, [getColumnValues]);

  return (
    <div className="inbox-page">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, width: "100%" }}>
        <Title text="WINZ TRANSACTION INBOX" />
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <span className="date-range-picker">
            Date Range:
            <DateRangePicker placeholder="DD/MM/YYYY - DD/MM/YYYY" cleanable={false} value={dateRange} onChange={handleDateRangeChange} placement="bottomEnd" />
          </span>
          <span className="record-type-toggle">
            Show History: <Checkbox id="show-history" checked={showPastRecords} onChange={handleRecordTypeToggle}></Checkbox>
          </span>
          {authService.isInRole(roles.ROLE_SUPER_ADMINISTRATOR) && (
            <span className="reset-button">
              <Button
                onClick={() => {
                  setDeleteModalOpen(true);
                }}
                color="red"
                text="RESET RUN"
                data-testid="reset-run-button"
              />
            </span>
          )}
        </div>
      </div>
      {isCheckPickerOpen && activeFilterColumnKey.current && (
        <FilterPanel
          activeColumn={activeFilterColumnKey.current}
          filterOptions={getValuesForActiveColumn()}
          selectedValues={filtersRef.current[activeFilterColumnKey.current] || []}
          onApply={applyCurrentFilters}
          onCancel={cancelFiltering}
          onFilterChange={onFilterChange}
        />
      )}
      <InboxTable
        data={data}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={handleSortColumn}
        loading={loading}
        activeFilters={activeFilters}
        onFilterClick={openCheckPickerForColumn}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
      <Modal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setResetConfirmText("");
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Delete Records</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to reset <b>ALL</b> records and PDF's?
          <br />
          This action cannot be undone.
          <br />
          <br />
          <div style={{ marginBottom: 8 }}>
            Please type <b>reset all</b> to confirm:
          </div>
          <Input
            type="text"
            value={resetConfirmText}
            onChange={(e) => setResetConfirmText(e.target.value)}
            style={{ width: "100%" }}
            disabled={deleting}
            autoFocus
            data-testid="reset-confirm-input"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="red"
            onClick={resetAll}
            appearance="primary"
            loading={deleting}
            disabled={deleting || resetConfirmText.trim().toLowerCase() !== "reset all"}
            text="Reset Run"
            data-testid="reset-confirm-button"
          />

          <Button
            onClick={() => {
              setDeleteModalOpen(false);
              setResetConfirmText("");
            }}
            color="blue"
            appearance="primary"
            disabled={deleting}
            text="Cancel"
            data-testid="reset-confirm-cancel-button"
          />
        </Modal.Footer>
      </Modal>
    </div>
  );
}

function InboxPage() {
  return (
    <InboxServiceProvider>
      <EntriesServiceProvider>
        <InboxPageContent />
      </EntriesServiceProvider>
    </InboxServiceProvider>
  );
}

export default InboxPage;
